import { useCallback, useEffect, useState } from "react";

export type SubPair = {
  outId: string;
  outName: string;
  outNumber: number | null;
  inId: string;
  inName: string;
  inNumber: number | null;
};

type KV = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const CHANGE = "livcg-sub-draft";
const key = (slug: string, side: "home" | "away") => `livcg-sub-draft:${slug}:${side}`;

/** Prepared-but-not-fired substitution pairs, per project and side, kept in
 *  this browser so closing the modal (a goal comes in mid-preparation) or
 *  reloading the page does not lose them. */
export function readDraft(store: KV, slug: string, side: "home" | "away"): SubPair[] {
  try {
    const v = JSON.parse(store.getItem(key(slug, side)) ?? "[]");
    return Array.isArray(v) ? (v as SubPair[]) : [];
  } catch {
    return [];
  }
}

export function writeDraft(store: KV, slug: string, side: "home" | "away", pairs: SubPair[]): void {
  if (pairs.length === 0) store.removeItem(key(slug, side));
  else store.setItem(key(slug, side), JSON.stringify(pairs));
}

/** The draft as React state; every hook instance (modal, Sub H/A badge)
 *  follows the same value. */
export function useSubDraft(slug: string, side: "home" | "away") {
  const [pairs, setLocal] = useState<SubPair[]>(() => readDraft(localStorage, slug, side));

  useEffect(() => {
    const sync = () => setLocal(readDraft(localStorage, slug, side));
    sync();
    window.addEventListener(CHANGE, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CHANGE, sync);
      window.removeEventListener("storage", sync);
    };
  }, [slug, side]);

  const setPairs = useCallback(
    (next: SubPair[] | ((prev: SubPair[]) => SubPair[])) => {
      const value = typeof next === "function" ? next(readDraft(localStorage, slug, side)) : next;
      writeDraft(localStorage, slug, side, value);
      window.dispatchEvent(new Event(CHANGE));
    },
    [slug, side],
  );

  return [pairs, setPairs] as const;
}
